import {
    BaseEntity,
    EntityContext,
    EntityType,
    generateDeterministicId,
    predicateDelegateFor,
} from "@archipad-js/core/orm";
import { OrderByPredicate } from "@archipad-js/core/query";
import { FileDescriptor } from "@archipad-js/core/utils";

import { BugEntity } from "@archipad-js/archipad-model";
import { BugNoteEntity } from "@archipad-js/archipad-model";
import { RemarkEntity } from "@archipad-js/archipad-model";
import { RemarkAnnotationEntity } from "@archipad-js/archipad-model";
import { BugAnnotationEntity } from "@archipad-js/archipad-model";
import { BugNoteAnnotationEntity } from "@archipad-js/archipad-model";

export type AnnotatedEntity = BugEntity | BugNoteEntity | RemarkEntity;
export type AnnotationEntity = BugAnnotationEntity | BugNoteAnnotationEntity | RemarkAnnotationEntity;

/**
 * Responsible of all CRUD operations on {@link AnnotationEntity annotations entities}.
 */
export class AnnotationService {
    makeAnnotationForAnnotated(parent: AnnotatedEntity): AnnotationEntity {
        const entityContext: EntityContext = parent.getContext();

        const annotationEntity: AnnotationEntity = entityContext.createEntity(
            `${parent.entityType.entityName}Annotation`,
        );
        annotationEntity.parentAnnotatedEntity = parent;
        annotationEntity.creationDate = new Date();
        return annotationEntity;
    }

    makeLegacyAnnotationForAnnotated(parent: AnnotatedEntity): AnnotationEntity {
        const entityContext: EntityContext = parent.getContext();
        const legacyStableId = this._getAnnotationId(parent);

        /**
         *  @WORKAROUND
         *  Currently getOrCreate but it must be create only
         *  Fix https://bigsool-archipad.atlassian.net/browse/AP-8061 before
         */
        let annotationEntity: AnnotationEntity | null = entityContext.getEntity(
            `${parent.entityType.entityName}Annotation`,
            legacyStableId,
        );
        if (!annotationEntity) {
            annotationEntity = entityContext.createEntity(
                `${parent.entityType.entityName}Annotation`,
                legacyStableId,
            ) as AnnotationEntity;
            annotationEntity.temp = true;
            annotationEntity.hasMetadata = false;
            annotationEntity.parentAnnotatedEntity = parent;
            annotationEntity.creationDate = parent.creationDate;
        }

        return annotationEntity;
    }

    getLegacyAnnotation(annotatedEntity: AnnotatedEntity): AnnotationEntity | null {
        const annotations = this.getAnnotations(annotatedEntity);
        if (annotations.length < 1) {
            return null;
        }

        const legacyAnnotationIndex = annotations.findIndex((annotation) => this.isLegacyAnnotationEntity(annotation));
        return legacyAnnotationIndex >= 0 ? annotations[legacyAnnotationIndex] : null;
    }

    /**
     * Check if the given entity is an AnnotationEntity
     */
    isAnnotationEntity(entity: BaseEntity): entity is AnnotationEntity {
        return (
            entity instanceof BugAnnotationEntity ||
            entity instanceof BugNoteAnnotationEntity ||
            entity instanceof RemarkAnnotationEntity
        );
    }

    /**
     * Check if annotation entity is the legacy annotation entity
     */
    isLegacyAnnotationEntity(annotationEntity: AnnotationEntity) {
        const isTemp = annotationEntity.temp === true;

        /**
         * @WORKAROUND
         * Check consistency
         * https://bigsool-archipad.atlassian.net/browse/AP-8061
         * Reactivate this sanity check when the bug has been fixed
         */
        /*const annotatedEntity = annotationEntity.parentAnnotatedEntity;
        const legacyAnnotationId = this._getAnnotationId(annotatedEntity);
        const hasLegacyStableId = annotationEntity.id === legacyAnnotationId
        if(isTemp !== hasLegacyStableId) {
            throw new IllegalStateError('Annotation entity illegal state');
        }*/

        return isTemp;
    }

    private _getAnnotationId(entity: AnnotatedEntity): AnnotationEntity["id"] {
        return generateDeterministicId(entity.id, "C4EAC4A905844BABA0145A3C01B018EC", "uuid");
    }

    /**
     * Returns the entity name of an annotation for a given annotated entity.
     *
     * @example Bug => BugAnnotation
     * @example Remark => RemarkAnnotation
     */
    getAnnotationEntityTypeForAnnotated(annotatedEntity: AnnotatedEntity): EntityType {
        const annotationsProperty = annotatedEntity.entityType.propertyForKeyPath("annotations");
        if (!annotationsProperty) {
            throw new Error(`Unable to find annotations property in "${annotatedEntity.entityType.entityName}"`);
        }

        if (!annotationsProperty.relation) {
            throw new Error(`Property "${annotationsProperty.name}" is not a relation`);
        }

        return annotationsProperty.target;
    }

    /**
     * Returns the annotations of an annotatedEntity sorted from newest first.
     *
     * @example AnnotatedEntity => Array<AnnotationEntity>
     * @example Bug => Array<BugAnnotationEntity>
     * @example Remark => Array<RemarkAnnotationEntity>
     */
    getAnnotations(annotatedEntity: AnnotatedEntity): Array<AnnotationEntity> {
        if (!annotatedEntity.annotations.length) {
            return [];
        }

        const entityContext = annotatedEntity.getContext();

        const annotationEntityType = this.getAnnotationEntityTypeForAnnotated(annotatedEntity);
        const annotationEntityName = annotationEntityType.entityName as keyof EntityTypeMap;

        const predicate = new OrderByPredicate(`creationDate desc, id asc`);
        const comparator = predicate.build(predicateDelegateFor(entityContext, annotationEntityName));

        const annotations: readonly AnnotationEntity[] = annotatedEntity.annotations;

        return annotations.filter((annotation) => annotation.hasAttachments() || annotation.temp).sort(comparator);
    }

    copyAnnotationsToAnotherAnnotatedEntity(annotatedEntity: AnnotatedEntity, newAnnotatedEntity: AnnotatedEntity) {
        if (annotatedEntity === null || newAnnotatedEntity === null) {
            throw new Error("Invalid annotated entities");
        }
        if (annotatedEntity === newAnnotatedEntity) {
            throw new Error("Cannot copy annotations to the same annotated entity");
        }
        for (const annotation of annotatedEntity.annotations) {
            const newAnnotation = this.makeAnnotationForAnnotated(newAnnotatedEntity);
            for (const [attributeKey, property] of Object.entries(annotation.entityType.metadata)) {
                if (property.calculated) {
                    continue;
                }
                if (property.relation === false && property.primaryKey) {
                    continue;
                }
                (newAnnotation as unknown as Record<string, unknown>)[attributeKey] = (
                    annotation as unknown as Record<string, unknown>
                )[attributeKey];
            }
            newAnnotation.parentAnnotatedEntity = newAnnotatedEntity; // ReSet the parent annotated entity
            for (const [attachmentKey, attachment] of Object.entries(annotation.getAttachments())) {
                newAnnotation.setAttachmentForKey(attachmentKey, attachment as FileDescriptor);
            }
        }
    }
}
